import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CalendarX2 } from "lucide-react";

import { plannable, planOption, placeOf, dayDate, planErrors, confirmedContacts, firstName, normalizeChecklist, normalizeTravel, CHECKLIST, MEETING_MAX } from "@/lib/v3Plans";
import { useCore } from "../useCore";
import { planCapabilities, planTravel, planChecklist } from "@/lib/memberPlans";
import { usePlanSave } from "../usePlanSave";
import { PlanSaveStatus } from "../components/PlanSaveStatus";
import { requiredCollection } from "../requiredCollection";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Actions, V3Field, StatusBanner, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard, V3Select, ResultTable, ResultRow } from "../components/v3app";
import { InfoBox } from "../components/v3people";
import { usePlanItems, Blocked, CheckRow, CheckList, NoPlan, enc } from "../components/v3plans";

const Aside = ({ title, text: body }) => <><AreaCard title={title} text={body} testid="plan-area" /><ResponsibleCard /></>;

// Loads plan items (and optionally confirmed contacts) and hands only complete data to the view.
function WithPlans({ contacts, capabilities, children }) {
  const res = usePlanItems();
  const conv = useCore(contacts ? "/member/conversations" : null, (d) => requiredCollection(d, "conversations"));
  const caps = useCore(capabilities ? "/member/plans/capabilities" : null, planCapabilities);
  const capError = caps.error && caps.error !== "notfound";
  if (res.error || (contacts && conv.error) || capError) return <ConnectionError onRetry={() => {
    if (res.error) res.reload();
    if (contacts && conv.error) conv.reload();
    if (capError) caps.reload();
  }} />;
  if (!res.items || (contacts && !conv.data) || (capabilities && !caps.data && !caps.error)) return <Loading />;
  return children(res.items, confirmedContacts(conv.data), caps.data || { travel: false, checklist: false, sharing: false });
}

// M27: a confirmed capabilities response and a readable owned plan are required before writing.
function PlanForm({ items, contacts, features }) {
  const [params] = useSearchParams();
  const options = plannable(items);
  const initial = options.find((i) => i.id === params.get("termin")) || options[0] || null;
  const [termin, setTermin] = useState(initial?.id || "");
  const [meetingPoint, setMeetingPoint] = useState(initial ? placeOf(initial) : "");
  const [time, setTime] = useState("");
  const [companion, setCompanion] = useState("");
  const [returnNote, setReturnNote] = useState("");
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const cur = options.find((i) => i.id === termin) || null;
  const path = features.travel && cur ? `/member/plans/${enc(cur.id)}/travel` : null;
  const stored = useCore(path, (d) => ({ travel: planTravel(d) }));
  const writer = usePlanSave(path, "travel");
  const resetWriter = writer.reset;
  const baseMeeting = cur ? placeOf(cur) : "";
  useEffect(() => {
    if (!features.travel) return;
    const t = stored.data?.travel;
    setMeetingPoint(t?.meeting_point_public || baseMeeting);
    setTime(t?.meet_time || ""); setReturnNote(t?.return_note || "");
    setCompanion(t?.participants[0]?.id || ""); setReview(false); setErrors({}); resetWriter();
  }, [termin, features.travel, stored.data, baseMeeting, resetWriter]);
  const busy = writer.busy;
  const ready = !features.travel || Boolean(stored.data && !stored.error);
  const mate = contacts.find((c) => c.id === companion) || null;
  const edit = (set) => (e) => { set(e.target.value); setReview(false); writer.reset(); };
  const pick = (e) => { const n = options.find((i) => i.id === e.target.value); setTermin(e.target.value); if (n) setMeetingPoint(placeOf(n)); setReview(false); writer.reset(); };
  const check = () => { const e = planErrors({ termin, meetingPoint, time, companion }, items, contacts); setErrors(e); setReview(!Object.keys(e).length); };
  const aside = <Aside title="Ausgewählter Vorgang" text={cur ? planOption(cur) : "Kein Termin ausgewählt"} />;
  if (!options.length) return <V3Columns layout="app" aside={aside} main={<NoPlan testid="plan-create-none" />} />;
  if (features.travel && stored.error) return <ConnectionError onRetry={stored.reload} />;
  if (!ready) return <Loading />;
  return (
    <V3Columns layout="app" aside={aside} main={<>
      <V3Select id="plan-termin" label="Treffen" value={termin} onChange={pick} disabled={busy} testid="plan-termin">
        {options.map((i) => <option key={i.id} value={i.id}>{planOption(i)}</option>)}
      </V3Select>
      <V3Field id="plan-meeting" label="Öffentlicher Treffpunkt" value={meetingPoint} onChange={edit(setMeetingPoint)} disabled={busy} maxLength={MEETING_MAX + 20} error={errors.meetingPoint}
        hint="Ein öffentlicher Ort – keine Wohnadresse." autoComplete="off" />
      <V3Field id="plan-time" label="Eigene Planzeit" inputMode="numeric" placeholder="z. B. 13:30" maxLength={5} value={time} onChange={edit(setTime)} disabled={busy} error={errors.time} hint="Uhrzeit (HH:MM), zu der du am Treffpunkt sein möchtest." autoComplete="off" />
      <V3Select id="plan-companion" label="Gemeinsame Anreise" value={companion} onChange={edit(setCompanion)} disabled={busy} testid="plan-companion" hint="Nur bestätigte Kontakte, die du gezielt auswählst." error={errors.companion}>
        <option value="">Allein anreisen</option>
        {contacts.map((c) => <option key={c.id} value={c.id}>{`${firstName(c.name)} gezielt dazu einladen`}</option>)}
      </V3Select>
      <V3Field id="plan-return" label="Rückweg (freiwillig)" value={returnNote} onChange={edit(setReturnNote)} disabled={busy} maxLength={300} hint="Auch diese Angabe sieht die ausgewählte Planperson. Keine Passdaten oder privaten Adressen." autoComplete="off" />
      {errors.termin && <p role="alert" data-testid="plan-termin-error" className="mb-[12px] text-[13px] font-bold text-red-800">{errors.termin}</p>}
      {review && <InfoBox title="Plan prüfen" testid="plan-review"
        text={`${planOption(cur)} · Treffpunkt ${meetingPoint.trim()} · ${time} Uhr · Rückweg: ${returnNote.trim() || "Keine Angabe"} · ${mate ? `Beim Speichern sieht ${firstName(mate.name)} diese Angaben und erhält eine Benachrichtigung` : "Anreise allein; diese Angaben bleiben privat"}. Bei einem Wechsel der Planperson musst du das Teilen der Checkliste neu auswählen.`} />}
      {review && !features.travel && <Blocked id="plan-save-blocked">Eigene Anreisepläne sind in dieser Umgebung noch nicht eingerichtet. Der Plan wird nicht gespeichert und niemand wird eingeladen.</Blocked>}
      <PlanSaveStatus writer={writer} />
      <V3Actions>
        {review ? features.travel ? <button type="button" disabled={busy} onClick={() => writer.save({ meeting_point_public: meetingPoint.trim(), meet_time: time, participant_ids: companion ? [companion] : [], return_note: returnNote.trim() })} data-testid="plan-save" className={V3_PRIMARY}>{busy ? "Wird geprüft …" : "Plan speichern"}</button>
          : <button type="button" disabled aria-describedby="plan-save-blocked" data-testid="plan-save" className={V3_PRIMARY}>Plan speichern</button>
          : <button type="button" onClick={check} data-testid="plan-check" className={V3_PRIMARY}>Plan prüfen</button>}
        {review ? <button type="button" disabled={busy} onClick={() => { setReview(false); writer.reset(); }} data-testid="plan-edit" className={V3_SECONDARY}>Ändern</button>
          : <Link to="/plaene" data-testid="plan-cancel" className={V3_SECONDARY}>Abbrechen</Link>}
      </V3Actions>
    </>} />
  );
}

export function PlanCreate() {
  const [params] = useSearchParams();
  return (
    <div data-m="M27" data-testid="plan-create">
      <V3Title title="Plan erstellen und gemeinsam anreisen" intro="Plane deine Anreise und teile nur bewusst ausgewählte Informationen." testid="plan-create-title" />
      <WithPlans contacts capabilities>{(items, contacts, features) => <PlanForm key={params.get("termin") || "default"} items={items} contacts={contacts} features={features} />}</WithPlans>
    </div>
  );
}

// M94: legacy Cores keep local ticks; a confirmed capability enables persistent, verified saves.
function Checklist({ item, features }) {
  const path = `/member/plans/${enc(item.id)}/checklist`;
  const saved = useCore(path, (d) => ({ c: normalizeChecklist(features.checklist ? { checklist: planChecklist(d) } : d) }));
  const [local, setLocal] = useState({});
  const writer = usePlanSave(features.checklist ? path : null, "checklist");
  const missing = saved.error === "notfound";
  if (saved.error && (!missing || features.checklist)) return <ConnectionError onRetry={saved.reload} />;
  if (!saved.data && !missing) return <Loading />;
  const base = writer.confirmed ? normalizeChecklist({ checklist: writer.confirmed }) : missing ? null : saved.data.c;
  const value = (k) => (k in local ? local[k] : k === "share" ? base?.share === true : base?.done[k] === true);
  const toggle = (k) => (e) => { setLocal((s) => ({ ...s, [k]: e.target.checked })); writer.reset(); };
  const done = CHECKLIST.filter(([k]) => value(k)).length;
  return (
    <V3Columns layout="detail" aside={<Aside text={`${item.title} · ${done} von ${CHECKLIST.length} erledigt`} />} main={<>
      {base === null && !missing && <div className="mb-[12px]"><Blocked id="plan-checklist-format">Die gespeicherte Checkliste ist nicht lesbar. Angezeigt wird eine leere Liste.</Blocked></div>}
      <CheckList label={`Checkliste für ${item.title}`}>
        {CHECKLIST.map(([k, title]) => (
          <CheckRow key={k} title={title} value={value(k) ? "Erledigt" : "Noch offen"} checked={value(k)} input={{ type: "checkbox", onChange: toggle(k), disabled: writer.busy, "aria-describedby": "plan-checklist-note" }} testid={`plan-check-${k}`} />
        ))}
        <CheckRow title="Mit Planperson teilen" value={value("share") ? "Mit der ausgewählten Begleitperson speichern" : "Privat speichern"} checked={value("share")}
          input={{ type: "checkbox", onChange: toggle("share"), disabled: writer.busy || (features.checklist && !features.sharing), "aria-describedby": "plan-checklist-note" }} testid="plan-check-share" />
      </CheckList>
      <p id="plan-checklist-note" className="mt-[12px] text-[14px] text-v3-muted">Erst Speichern übernimmt deine Auswahl. Teilen gilt nur für die gezielt gewählte, weiterhin bestätigte Begleitperson deines Anreiseplans.</p>
      {features.checklist ? <p className="mt-[8px] text-[13px] text-v3-muted" data-testid="plan-checklist-confirmed-share">Zuletzt bestätigte Auswahl: {base?.share ? "Teilen mit der Planperson ausgewählt; Zugriff wird erneut geprüft" : "privat"}.</p>
        : <Blocked id="plan-checklist-blocked">Die Checkliste kann in dieser Umgebung noch nicht gespeichert werden. Häkchen gelten nur in dieser Ansicht, werden mit niemandem geteilt und gehen beim Verlassen verloren.</Blocked>}
      <PlanSaveStatus writer={writer} />
      <V3Actions>
        {features.checklist ? <button type="button" disabled={writer.busy} onClick={() => writer.save({ items: CHECKLIST.map(([key]) => ({ key, done: value(key) })), share_with_plan_members: value("share") })} data-testid="plan-checklist-save" className={V3_PRIMARY}>{writer.busy ? "Wird geprüft …" : "Checkliste speichern"}</button>
          : <button type="button" disabled aria-describedby="plan-checklist-blocked" data-testid="plan-checklist-save" className={V3_PRIMARY}>Checkliste speichern</button>}
        <Link to={`/plaene/${enc(item.id)}`} data-testid="plan-checklist-back" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
    </>} />
  );
}

function ChecklistPick({ items, features }) {
  const [params] = useSearchParams();
  const options = plannable(items);
  const cur = options.find((i) => i.id === params.get("termin")) || options[0] || null;
  if (!cur) return <V3Columns layout="detail" aside={<Aside text="Kein Termin ausgewählt" />} main={<NoPlan testid="plan-checklist-none" />} />;
  return <Checklist key={cur.id} item={cur} features={features} />;
}

export function PlanChecklist() {
  return (
    <div data-m="M94" data-testid="plan-checklist">
      <V3Title title="Persönliche Checkliste" intro="Deine persönliche Liste hilft dir bei der Vorbereitung." testid="plan-checklist-title" />
      <WithPlans capabilities>{(items, _contacts, features) => <ChecklistPick items={items} features={features} />}</WithPlans>
    </div>
  );
}

// M95: activity and public meeting point from GET /member/plans; own plan time and way back need GET …/travel (D-80).
function Travel({ item, features }) {
  const res = useCore(`/member/plans/${enc(item.id)}/travel`, (d) => ({ t: normalizeTravel(features.travel ? { travel: planTravel(d) } : d) }));
  const missing = res.error === "notfound";
  if (missing && features.travel) return <PlanMissing />;
  if (res.error && !missing) return <ConnectionError onRetry={res.reload} />;
  if (!res.data && !missing) return <Loading />;
  const t = missing ? null : res.data.t;
  const noPlan = features.travel && t === null;
  const na = missing ? "Noch nicht verfügbar" : noPlan ? "Noch nicht festgelegt" : t === null ? "Nicht lesbar" : "";
  const id = enc(item.id);
  return (<>
    <ResultTable testid="plan-detail-rows" label={`Anreiseplan für ${item.title}`}>
      <ResultRow initial="A" title="Aktivität" context={item.title} to={`/aktivitaeten/${id}`} testid="plan-row-activity" />
      <ResultRow initial="T" title="Treffpunkt" context={t?.meetingPoint || placeOf(item) || "Ort folgt"} to={`/aktivitaeten/${id}/zugang`} testid="plan-row-meeting" />
      <ResultRow initial="E" title="Eigene Planzeit" context={na || (t.time ? `${dayDate(item.day)} · ${t.time} Uhr` : "Noch nicht festgelegt")} to={`/plaene/neu?termin=${id}`} testid="plan-row-time" />
      <ResultRow initial="R" title="Rückweg" context={na || t.returnNote || "Noch nicht ergänzt"} to={`/plaene/checkliste?termin=${id}`} testid="plan-row-return" />
    </ResultTable>
    {missing && <Blocked id="plan-detail-unavailable">Eigene Anreisepläne liefert der gemeinsame Core noch nicht. Angezeigt werden nur Termin und öffentlicher Treffpunkt der Aktivität; Fahrplandaten werden nicht abgerufen.</Blocked>}
    <V3Actions><Link to={`/plaene/neu?termin=${id}`} data-testid="plan-detail-edit" className={V3_PRIMARY}>Plan bearbeiten</Link></V3Actions>
  </>);
}

function PlanMissing() {
  return (<>
    <StatusBanner tone="sand" icon={CalendarX2} title="Termin nicht verfügbar" text="Dieser Termin gehört nicht (mehr) zu deinen Plänen." testid="plan-detail-missing" />
    <V3Actions><Link to="/plaene/liste" data-testid="plan-detail-to-list" className={V3_PRIMARY}>Zu meinen Plänen</Link></V3Actions>
  </>);
}

export function PlanDetail() {
  const { id } = useParams();
  return (
    <div data-m="M95" data-testid="plan-detail">
      <V3Title title="Anreiseplan im Detail" intro="Prüfe deine eigene Planung und den aktuellen Fahrplan vor der Abreise." testid="plan-detail-title" />
      <WithPlans capabilities>{(items, _contacts, features) => {
        const item = items.find((i) => i.kind === "plan" && i.id === String(id));
        return item ? <Travel key={item.id} item={item} features={features} /> : <PlanMissing />;
      }}</WithPlans>
    </div>
  );
}
